import { Controller } from "@hotwired/stimulus"
import BuilderApi from "../builder/api"

// Section presets, grouped by category. Previews are rendered by the server's
// own renderer into a scaled iframe, lazily and only for the open category:
// generating thumbnails offline would need a headless browser and would drift
// from the presets.
export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.open = null
    this.root.addEventListener("builder:ready", () => this.render(), { once: true })
    if (this.root.builder) this.render()
  }

  get builder() { return this.root.builder }
  get store() { return this.builder.store }

  render() {
    const categories = this.builder.schema.sections || []
    this.listTarget.replaceChildren(...categories.map((category) => this.category(category)))
  }

  category(category) {
    const wrap = document.createElement("div")
    wrap.className = "mb-1"

    const header = document.createElement("button")
    header.type = "button"
    header.dataset.action = "builder-sections#toggle"
    header.dataset.category = category.key
    header.textContent = category.name
    header.className =
      "flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs font-semibold " +
      "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"

    const body = document.createElement("div")
    body.className = "space-y-2 py-2"
    body.hidden = this.open !== category.key
    if (!body.hidden) category.presets.forEach((preset) => body.append(this.preset(preset)))

    wrap.append(header, body)
    return wrap
  }

  preset(preset) {
    const card = document.createElement("button")
    card.type = "button"
    card.dataset.action = "builder-sections#insert"
    card.dataset.key = preset.key
    card.className =
      "block w-full overflow-hidden rounded-md border border-slate-200 text-left " +
      "hover:border-cyan-400 dark:border-slate-700"

    const frame = document.createElement("iframe")
    frame.className = "pointer-events-none h-24 w-full border-0"
    frame.setAttribute("tabindex", "-1")
    frame.setAttribute("aria-hidden", "true")
    frame.setAttribute("loading", "lazy")
    frame.src = `${this.builder.urls.canvas}?preset=${encodeURIComponent(preset.key)}`

    const label = document.createElement("span")
    label.className = "block px-2 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-300"
    label.textContent = preset.name

    card.append(frame, label)
    return card
  }

  toggle(event) {
    const key = event.currentTarget.dataset.category
    this.open = this.open === key ? null : key
    this.render()
  }

  // Presets are appended to the page; a section can only live at the root.
  async insert(event) {
    const key = event.currentTarget.dataset.key
    const { ok, payload } = await BuilderApi.createSection(this.builder.urls.sections, key)
    if (!ok) return

    payload.node.__html = payload.html
    const root = this.store.root
    this.store.insert(payload.node, root.id, (root.children || []).length)
  }
}
